import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import z from 'zod';

import type { DateRangePreset } from '@/api/types/usage';
import { LayoutDefault } from '@/components/LayoutDefault';
import { LayoutHeader } from '@/components/LayoutHeader';
import { LayoutHeaderTitle } from '@/components/LayoutHeaderTitle';
import type { UsageTab } from '@/components/UsageTracking/UsageBreakdownTabs';
import { UsageTrackingPage } from '@/components/UsageTracking/UsageTrackingPage';
import {
  DEFAULT_CODEBASE_SORT_BY,
  DEFAULT_CODEBASE_SORT_ORDER,
  DEFAULT_ENGAGEMENT_SORT_BY,
  DEFAULT_ENGAGEMENT_SORT_ORDER,
  DEFAULT_MACHINE_SORT_BY,
  DEFAULT_MACHINE_SORT_ORDER,
  DEFAULT_SERVICE_SORT_BY,
  DEFAULT_SERVICE_SORT_ORDER,
  DEFAULT_TOOL_SORT_BY,
  DEFAULT_TOOL_SORT_ORDER,
  DEFAULT_USER_SORT_BY,
  DEFAULT_USER_SORT_ORDER,
} from '@/components/UsageTracking/constants';
import { useUser } from '@/hooks/useUser';

const usageTabValues: [UsageTab, ...UsageTab[]] = [
  'by-tool',
  'by-user',
  'by-machine',
  'by-codebase',
  'by-service',
  'engagement',
];
const dateRangePresetValues: [DateRangePreset, ...DateRangePreset[]] = ['7d', '30d', '90d', 'custom'];

const sortOrderValues = ['asc', 'desc'] as const;

export const usageSearchSchema = z.object({
  preset: z.enum(dateRangePresetValues).default('30d').catch('30d'),
  startDate: z.string().catch('').optional(),
  endDate: z.string().catch('').optional(),
  codebase: z.string().catch('').optional(),
  machine: z.string().catch('').optional(),
  user: z.string().catch('').optional(),
  team: z.string().catch('').optional(),
  tab: z.enum(usageTabValues).default('by-tool').catch('by-tool'),
  pageIndex: z.number().default(0).catch(0),
  pageSize: z.number().default(10).catch(10),
  engagementSortBy: z.string().catch(DEFAULT_ENGAGEMENT_SORT_BY).optional(),
  engagementSortOrder: z.enum(sortOrderValues).catch(DEFAULT_ENGAGEMENT_SORT_ORDER).optional(),
  toolSortBy: z.string().catch(DEFAULT_TOOL_SORT_BY).optional(),
  toolSortOrder: z.enum(sortOrderValues).catch(DEFAULT_TOOL_SORT_ORDER).optional(),
  userSortBy: z.string().catch(DEFAULT_USER_SORT_BY).optional(),
  userSortOrder: z.enum(sortOrderValues).catch(DEFAULT_USER_SORT_ORDER).optional(),
  codebaseSortBy: z.string().catch(DEFAULT_CODEBASE_SORT_BY).optional(),
  codebaseSortOrder: z.enum(sortOrderValues).catch(DEFAULT_CODEBASE_SORT_ORDER).optional(),
  serviceSortBy: z.string().catch(DEFAULT_SERVICE_SORT_BY).optional(),
  serviceSortOrder: z.enum(sortOrderValues).catch(DEFAULT_SERVICE_SORT_ORDER).optional(),
  machineSortBy: z.string().catch(DEFAULT_MACHINE_SORT_BY).optional(),
  machineSortOrder: z.enum(sortOrderValues).catch(DEFAULT_MACHINE_SORT_ORDER).optional(),
});

export const Route = createFileRoute('/$organizationName/usage')({
  component: SecuredUsageTrackingPage,
  validateSearch: usageSearchSchema,
});

function SecuredUsageTrackingPage() {
  return (
    <LayoutDefault
      showBorder
      navbarTitle="Usage"
      header={
        <LayoutHeader>
          <LayoutHeaderTitle title="Usage" />
        </LayoutHeader>
      }
    >
      <UsageTrackingPageWithTitle />
    </LayoutDefault>
  );
}

function UsageTrackingPageWithTitle() {
  const { organizationDisplayName } = useUser();

  useEffect(() => {
    document.title = `${organizationDisplayName || 'Driver'} - Usage`;
  }, [organizationDisplayName]);

  return <UsageTrackingPage />;
}
